import { useFragment } from "@/generated";
import { AddLessonDocument, LessonFieldsFragmentDoc, type LessonInput } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for adding new lessons with intelligent Apollo cache updates.
 *
 * @example
 * const { addLesson, loading } = useAddLesson();
 * const newLessons = await addLesson({
 *   name: "New Lesson",
 *   grammarLimit: "basic",
 *   parentId: ["parentProjectId"],
 *   isPublic: true
 * });
 */
export function useAddLesson() {
  const [addLessonMutation, { loading }] = useMutation(AddLessonDocument);

  const addLesson = async (input: LessonInput, thumbnailFile?: File | null) => {
    return withToast(
      async () => {
        const result = await addLessonMutation({
          variables: { input, thumbnailFile: thumbnailFile || undefined },
          update(cache, { data }) {
            if (!data) return;

            // Unmasked all returned lessons
            const unmaskedLessons = data.addLesson.map((lesson) =>
              useFragment(LessonFieldsFragmentDoc, lesson)
            );

            // Group lessons by parentId
            const lessonsByParentIdMap = new Map<string | null, typeof unmaskedLessons>();

            for (const lesson of unmaskedLessons) {
              const key = lesson.parentId ?? null;

              const lessonsForParent = lessonsByParentIdMap.get(key) ?? [];

              lessonsForParent.push(lesson);
              lessonsByParentIdMap.set(key, lessonsForParent);
            }

            // Batch all cache modifications
            cache.batch({
              update(cache) {
                // Normalized cache

                for (const lesson of unmaskedLessons) {
                  const id = cache.identify(lesson);
                  if (id) {
                    cache.writeFragment({
                      id,
                      fragment: LessonFieldsFragmentDoc,
                      data: lesson,
                    });
                  }
                }

                // Update each parentId field in ROOT_QUERY
                for (const [parentId, lessons] of lessonsByParentIdMap.entries()) {
                  const newRefs = lessons
                    .map((p) => cache.identify(p))
                    .filter(Boolean)
                    .map((id) => ({ __ref: id }));

                  cache.modify({
                    id: "ROOT_QUERY",
                    fields: {
                      lessonsByParentId(existingData, { storeFieldName }) {
                        const key =
                          parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                        const isTrashKey = '"isTrash":false';

                        if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                          return existingData;
                        if (!existingData?.items) return existingData;

                        return {
                          ...existingData,
                          items: [...newRefs, ...existingData.items],
                          total: (existingData.total || 0) + newRefs.length,
                        };
                      },
                    },
                  });

                  if (parentId !== null) {
                    cache.modify({
                      id: `Project:${parentId}`,
                      fields: {
                        itemCount(existingData = 0) {
                          return existingData + newRefs.length;
                        },
                      },
                    });
                  }
                }
              },
            });
          },
        });

        const maskedLessons = result.data?.addLesson ?? [];
        const newLessons = maskedLessons.map((lesson) =>
          useFragment(LessonFieldsFragmentDoc, lesson)
        );

        return newLessons;
      },
      {
        loading: "Creating lesson...",
        success: `Successfully created lesson "${input.name}"`,
        error: "Failed to add lesson. Please try again.",
      },
      "addLesson"
    );
  };

  return {
    addLesson,
    loading,
  };
}
